import React, { useState, useEffect } from 'react'
import {
  PlusIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  BanknotesIcon,
  CalendarIcon,
  FunnelIcon,
  DocumentChartBarIcon,
  CurrencyDollarIcon,
  ClockIcon
} from '@heroicons/react/24/outline'
import { cashFlowAPI, type CashFlowEntry, type CashFlowSummary, type CashFlowAnalysis } from '@/services/api'

const CashFlowPage: React.FC = () => {
  const [entries, setEntries] = useState<CashFlowEntry[]>([])
  const [summary, setSummary] = useState<CashFlowSummary | null>(null)
  const [analysis, setAnalysis] = useState<CashFlowAnalysis | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedPeriod, setSelectedPeriod] = useState<'current_month' | 'last_month' | 'year' | 'custom'>('year')
  const [filterType, setFilterType] = useState<'ALL' | 'income' | 'expense'>('ALL')
  const [customStartDate, setCustomStartDate] = useState('')
  const [customEndDate, setCustomEndDate] = useState('')

  // Função para calcular datas baseado no período selecionado
  const getDateRange = () => {
    const now = new Date()
    const year = now.getFullYear()
    const month = now.getMonth()

    switch (selectedPeriod) {
      case 'current_month':
        return {
          start: `${year}-${String(month + 1).padStart(2, '0')}-01`,
          end: new Date(year, month + 1, 0).toISOString().split('T')[0]
        }
      case 'last_month':
        const lastMonth = month === 0 ? 11 : month - 1
        const lastMonthYear = month === 0 ? year - 1 : year
        return {
          start: `${lastMonthYear}-${String(lastMonth + 1).padStart(2, '0')}-01`,
          end: new Date(lastMonthYear, lastMonth + 1, 0).toISOString().split('T')[0]
        }
      case 'custom':
        return {
          start: customStartDate || `${year}-01-01`,
          end: customEndDate || `${year}-12-31`
        }
      case 'year':
      default:
        return {
          start: `${year}-01-01`,
          end: `${year}-12-31`
        }
    }
  }

  useEffect(() => {
    const fetchCashFlowData = async () => {
      try {
        setLoading(true)
        const { start, end } = getDateRange()

        console.log('[CASH FLOW] Selected Period:', selectedPeriod)
        console.log('[CASH FLOW] Date Range:', start, 'to', end)

        // Load cash flow data from real API with date filters
        const [entriesData, summaryData, analysisData] = await Promise.all([
          cashFlowAPI.getEntries(start, end),
          cashFlowAPI.getSummary(start, end),
          cashFlowAPI.getAnalysis()
        ])

        setEntries(entriesData)
        setSummary(summaryData)
        setAnalysis(analysisData)
      } catch (err: any) {
        setError(err.message || 'Erro ao carregar fluxo de caixa')
      } finally {
        setLoading(false)
      }
    }

    fetchCashFlowData()
  }, [selectedPeriod, customStartDate, customEndDate])

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  const getTypeColor = (type: string) => {
    return type === 'income' ? 'text-green-600 bg-green-50' : 'text-red-600 bg-red-50'
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800'
      case 'projected':
        return 'bg-blue-100 text-blue-800'
      case 'pending':
        return 'bg-yellow-100 text-yellow-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'Confirmado'
      case 'projected':
        return 'Projetado'
      case 'pending':
        return 'Pendente'
      default:
        return status
    }
  }

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'positive':
        return <ArrowTrendingUpIcon className="w-5 h-5 text-green-500" />
      case 'negative':
        return <ArrowTrendingDownIcon className="w-5 h-5 text-red-500" />
      default:
        return <BanknotesIcon className="w-5 h-5 text-gray-500" />
    }
  }

  const filteredEntries = entries.filter(entry => {
    if (filterType === 'ALL') return true
    return entry.type === filterType
  })

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded mb-4"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-32 bg-gray-100 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <h3 className="text-red-800 font-semibold mb-2">Erro ao carregar fluxo de caixa</h3>
          <p className="text-red-600">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ChartBarIcon className="w-7 h-7 text-blue-600" />
            Fluxo de Caixa
          </h1>
          <p className="text-gray-600">Controle e projeção de entradas e saídas</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg hover:from-blue-700 hover:to-purple-700 flex items-center gap-2 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <PlusIcon className="w-5 h-5" />
            Nova Entrada
          </button>
        </div>
      </div>

      {/* Period Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <CalendarIcon className="w-5 h-5 text-gray-600" />
          <span className="text-sm font-medium text-gray-700">Período:</span>

          <button
            onClick={() => setSelectedPeriod('current_month')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedPeriod === 'current_month'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Mês Atual
          </button>

          <button
            onClick={() => setSelectedPeriod('last_month')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedPeriod === 'last_month'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Mês Passado
          </button>

          <button
            onClick={() => setSelectedPeriod('year')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedPeriod === 'year'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Ano Completo
          </button>

          <button
            onClick={() => setSelectedPeriod('custom')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedPeriod === 'custom'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Personalizado
          </button>

          {selectedPeriod === 'custom' && (
            <div className="flex items-center gap-2 ml-4">
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-gray-500">até</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}
        </div>
      </div>

      {/* Analysis Cards */}
      {analysis && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Saldo Atual</p>
                <p className="text-2xl font-bold">{formatCurrency(analysis.current_balance)}</p>
              </div>
              <BanknotesIcon className="w-8 h-8 text-blue-200" />
            </div>
          </div>

          <div className={`rounded-xl p-6 text-white ${
            analysis.month_flow >= 0
              ? 'bg-gradient-to-r from-green-500 to-green-600'
              : 'bg-gradient-to-r from-red-500 to-red-600'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${analysis.month_flow >= 0 ? 'text-green-100' : 'text-red-100'}`}>
                  Fluxo do Mês
                </p>
                <p className="text-2xl font-bold">{formatCurrency(analysis.month_flow)}</p>
              </div>
              {getTrendIcon(analysis.trend)}
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Taxa de Queima/Mês</p>
                <p className="text-2xl font-bold">{formatCurrency(analysis.burn_rate)}</p>
              </div>
              <ArrowTrendingDownIcon className="w-8 h-8 text-purple-200" />
            </div>
          </div>

          <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm">Runway</p>
                <p className="text-2xl font-bold">{analysis.runway_days} dias</p>
              </div>
              <ClockIcon className="w-8 h-8 text-orange-200" />
            </div>
          </div>
        </div>
      )}

      {/* Summary */}
      {summary && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Resumo - {summary.period}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <div className="text-center">
              <p className="text-sm text-gray-600">Saldo Inicial</p>
              <p className="text-lg font-semibold text-gray-900">
                {formatCurrency(summary.opening_balance)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600">Receitas</p>
              <p className="text-lg font-semibold text-green-600">
                {formatCurrency(summary.total_income)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600">Despesas</p>
              <p className="text-lg font-semibold text-red-600">
                {formatCurrency(summary.total_expenses)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600">Fluxo Líquido</p>
              <p className={`text-lg font-semibold ${
                summary.net_flow >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {formatCurrency(summary.net_flow)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600">Saldo Final</p>
              <p className="text-lg font-semibold text-gray-900">
                {formatCurrency(summary.closing_balance)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600">Projeção</p>
              <p className={`text-lg font-semibold ${
                summary.projected_balance >= summary.closing_balance ? 'text-green-600' : 'text-red-600'
              }`}>
                {formatCurrency(summary.projected_balance)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 p-1 shadow-sm">
        <div className="flex space-x-1">
          <button
            onClick={() => setFilterType('ALL')}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
              filterType === 'ALL'
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Todas ({entries.length})
          </button>
          <button
            onClick={() => setFilterType('income')}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
              filterType === 'income'
                ? 'bg-green-600 text-white'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Receitas ({entries.filter(e => e.type === 'income').length})
          </button>
          <button
            onClick={() => setFilterType('expense')}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
              filterType === 'expense'
                ? 'bg-red-600 text-white'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Despesas ({entries.filter(e => e.type === 'expense').length})
          </button>
        </div>
      </div>

      {/* Entries Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Movimentações</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Descrição
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Categoria
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Projeto/Centro
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredEntries.map((entry) => (
                <tr key={entry.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(entry.date).toLocaleDateString('pt-BR')}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{entry.description}</div>
                    <div className="text-sm text-gray-500">
                      {entry.account} {entry.is_recurring && '• Recorrente'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {entry.category}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`text-sm font-medium ${
                      entry.type === 'income' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {entry.type === 'income' ? '+' : '-'}{formatCurrency(entry.amount)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(entry.status)}`}>
                      {getStatusLabel(entry.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {entry.project_name || entry.cost_center_name || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredEntries.length === 0 && (
          <div className="text-center py-12">
            <ChartBarIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma movimentação encontrada</h3>
            <p className="text-gray-500 mb-6">
              Não foram encontradas movimentações para o período selecionado
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
            >
              Adicionar Primeira Entrada
            </button>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Nova Movimentação</h3>
            <form className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data *
                  </label>
                  <input
                    type="date"
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo *
                  </label>
                  <select
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Selecione</option>
                    <option value="income">Receita</option>
                    <option value="expense">Despesa</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Descrição da movimentação"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valor (R$) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0,00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Categoria *
                  </label>
                  <select
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Selecione uma categoria</option>
                    <option value="vendas">Vendas</option>
                    <option value="servicos">Serviços</option>
                    <option value="pessoal">Pessoal</option>
                    <option value="tecnologia">Tecnologia</option>
                    <option value="marketing">Marketing</option>
                    <option value="operacional">Operacional</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Conta *
                  </label>
                  <select
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Selecione uma conta</option>
                    <option value="conta-corrente">Conta Corrente</option>
                    <option value="poupanca">Poupança</option>
                    <option value="cartao-corporativo">Cartão Corporativo</option>
                    <option value="dinheiro">Dinheiro</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="confirmed">Confirmado</option>
                    <option value="projected">Projetado</option>
                    <option value="pending">Pendente</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Projeto (opcional)
                  </label>
                  <select className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">Nenhum projeto</option>
                    <option value="1">Sistema de Vendas Online</option>
                    <option value="2">Migração para Cloud</option>
                    <option value="3">Campanha Marketing Digital</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Centro de Custo (opcional)
                  </label>
                  <select className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">Nenhum centro de custo</option>
                    <option value="1">TI</option>
                    <option value="2">Marketing</option>
                    <option value="3">Vendas</option>
                    <option value="4">Administrativo</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Movimentação recorrente</span>
                </label>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Adicionar Movimentação
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default CashFlowPage
